// Service Worker Registration for Willovate Resto PWA

const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    window.location.hostname === "[::1]" ||
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

export function register(config?: Config) {
  // In development, disable service worker to prevent caching issues
  if (process.env.NODE_ENV === 'development') {
    return;
  }

  if ('serviceWorker' in navigator) {
    // Wait for the page to load
    window.addEventListener("load", () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // This is running on localhost. Check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);

        navigator.serviceWorker.ready.then(() => {
        });
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === "installed") {
            if (navigator.serviceWorker.controller) {
              // New content is available; automatically activate it

              // Tell the service worker to skip waiting and activate immediately
              registration.waiting?.postMessage({ type: "SKIP_WAITING" });

              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // Content is cached for offline use (first install)

              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      console.error("[PWA] Error during service worker registration:", error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  // Prevent infinite reload loops - check if we've already attempted reload recently
  const lastReloadTime = sessionStorage.getItem('sw-last-reload');
  const now = Date.now();
  
  if (lastReloadTime && (now - parseInt(lastReloadTime)) < 5000) {
    return;
  }

  // Check if the service worker can be found
  fetch(swUrl, {
    headers: { "Service-Worker": "script" },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file
      const contentType = response.headers.get("content-type");
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf("javascript") === -1)
      ) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            // Mark reload time to prevent loops
            sessionStorage.setItem('sw-last-reload', now.toString());
            // Only reload once
            if (!sessionStorage.getItem('sw-reloaded')) {
              sessionStorage.setItem('sw-reloaded', 'true');
              window.location.reload();
            }
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
    });
}

export function unregister() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  }
}

// PWA Install Prompt Handler
export function setupInstallPrompt() {
  let deferredPrompt: any;

  window.addEventListener("beforeinstallprompt", (e) => {
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    // Store the event so it can be triggered later
    deferredPrompt = e;

    // Show install button or notification

    // Dispatch custom event that components can listen to
    const installEvent = new CustomEvent("pwa-install-available", {
      detail: deferredPrompt,
    });
    window.dispatchEvent(installEvent);
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;

    // Track installation (optional analytics)
    const installedEvent = new CustomEvent("pwa-installed");
    window.dispatchEvent(installedEvent);
  });

  return {
    showInstallPrompt: () => {
      if (deferredPrompt) {
        deferredPrompt.prompt();
        deferredPrompt.userChoice.then((choiceResult: any) => {
          if (choiceResult.outcome === "accepted") {
          } else {
          }
          deferredPrompt = null;
        });
      }
    },
  };
}
